// useRoomSettings — read+mutate helpers for a single room's metadata.
//
// Co-locates the things the `RoomSettings` panel needs in one callback
// surface so the component stays declarative: read the room via
// `subscribeRooms` (already in MatrixContext), call the verbs here to
// mutate. The hook does not hold its own state — it's a handle, not a
// store.

import { useCallback, useMemo } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { setChannelType as matrixSetChannelType, type ChannelType } from "~/lib/channel-type";
import {
  banUser as matrixBanUser,
  archiveRoom as matrixArchiveRoom,
  canSendStateEvent as matrixCanSendStateEvent,
  getMembers as matrixGetMembers,
  kickUser as matrixKickUser,
  setRoomAvatar as matrixSetRoomAvatar,
  setRoomName as matrixSetRoomName,
  setRoomTopic as matrixSetRoomTopic,
  unarchiveRoom as matrixUnarchiveRoom,
  uploadContent as matrixUploadContent,
  ARCHIVED_EVENT_TYPE,
  POWER_LEVELS_EVENT_TYPE,
  type RoomMember,
} from "~/lib/matrix";

/**
 * Power-level thresholds the UI reads. Matching Matrix defaults:
 * 0 = member, 50 = moderator, 100 = admin. Role labels in MemberList
 * and gating checks in RoomSettings reference these.
 */
export const POWER_LEVEL_ADMIN = 100;
export const POWER_LEVEL_MODERATOR = 50;

export interface RoomSettingsActions {
  setName: (next: string) => Promise<void>;
  setTopic: (next: string) => Promise<void>;
  setAvatarFromFile: (file: File) => Promise<string>;
  setChannelType: (next: ChannelType) => Promise<void>;
  getMembers: () => RoomMember[];
  kick: (userId: string, reason?: string) => Promise<void>;
  ban: (userId: string, reason?: string) => Promise<void>;
  archive: () => Promise<void>;
  unarchive: () => Promise<void>;
  canSetName: () => boolean;
  canSetTopic: () => boolean;
  canSetAvatar: () => boolean;
  canSetChannelType: () => boolean;
  canArchive: () => boolean;
  canManageMembers: () => boolean;
}

export function useRoomSettings(roomId: string): RoomSettingsActions {
  const { client, userId } = useMatrix();

  const requireClient = useCallback(() => {
    if (!client) throw new Error("useRoomSettings: Matrix client is not ready");
    return client;
  }, [client]);

  const setName = useCallback(
    async (next: string) => {
      await matrixSetRoomName(requireClient(), roomId, next);
    },
    [requireClient, roomId],
  );

  const setTopic = useCallback(
    async (next: string) => {
      await matrixSetRoomTopic(requireClient(), roomId, next);
    },
    [requireClient, roomId],
  );

  const setAvatarFromFile = useCallback(
    async (file: File): Promise<string> => {
      const c = requireClient();
      const { mxc } = await matrixUploadContent(c, file);
      await matrixSetRoomAvatar(c, roomId, mxc);
      return mxc;
    },
    [requireClient, roomId],
  );

  const setChannelType = useCallback(
    async (next: ChannelType) => {
      await matrixSetChannelType(requireClient(), roomId, next);
    },
    [requireClient, roomId],
  );

  const getMembers = useCallback((): RoomMember[] => {
    if (!client) return [];
    return matrixGetMembers(client, roomId);
  }, [client, roomId]);

  const kick = useCallback(
    async (targetUserId: string, reason?: string) => {
      await matrixKickUser(requireClient(), roomId, targetUserId, reason);
    },
    [requireClient, roomId],
  );

  const ban = useCallback(
    async (targetUserId: string, reason?: string) => {
      await matrixBanUser(requireClient(), roomId, targetUserId, reason);
    },
    [requireClient, roomId],
  );

  const archive = useCallback(async () => {
    if (!userId) throw new Error("archive: Matrix user id is not ready");
    await matrixArchiveRoom(requireClient(), roomId, userId);
  }, [requireClient, roomId, userId]);

  const unarchive = useCallback(async () => {
    if (!userId) throw new Error("unarchive: Matrix user id is not ready");
    await matrixUnarchiveRoom(requireClient(), roomId, userId);
  }, [requireClient, roomId, userId]);

  const canWrite = useCallback(
    (eventType: string): boolean => {
      if (!client || !userId) return false;
      return matrixCanSendStateEvent(client, roomId, userId, eventType);
    },
    [client, userId, roomId],
  );

  const canSetName = useCallback(() => canWrite("m.room.name"), [canWrite]);
  const canSetTopic = useCallback(() => canWrite("m.room.topic"), [canWrite]);
  const canSetAvatar = useCallback(() => canWrite("m.room.avatar"), [canWrite]);
  const canSetChannelType = useCallback(() => canWrite("com.nurion.channel_type"), [canWrite]);
  const canArchive = useCallback(
    () => canWrite(ARCHIVED_EVENT_TYPE) && canWrite(POWER_LEVELS_EVENT_TYPE),
    [canWrite],
  );
  const canManageMembers = useCallback(() => {
    if (!client || !userId) return false;
    const me = matrixGetMembers(client, roomId).find((m) => m.userId === userId);
    return (me?.powerLevel ?? 0) >= POWER_LEVEL_MODERATOR;
  }, [client, userId, roomId]);

  // Wrap in useMemo so the returned handle is reference-stable when
  // the dependency set hasn't changed. Consumers that depend on
  // `[settings]` in a useEffect (e.g. MemberList) would otherwise
  // re-run on every parent render and, when `getMembers()` returns a
  // fresh array, trigger an infinite setMembers → re-render loop.
  return useMemo<RoomSettingsActions>(
    () => ({
      setName,
      setTopic,
      setAvatarFromFile,
      setChannelType,
      getMembers,
      kick,
      ban,
      archive,
      unarchive,
      canSetName,
      canSetTopic,
      canSetAvatar,
      canSetChannelType,
      canArchive,
      canManageMembers,
    }),
    [
      setName,
      setTopic,
      setAvatarFromFile,
      setChannelType,
      getMembers,
      kick,
      ban,
      archive,
      unarchive,
      canSetName,
      canSetTopic,
      canSetAvatar,
      canSetChannelType,
      canArchive,
      canManageMembers,
    ],
  );
}
